/**
 * stylesLayout.js — 미팅지도 레이아웃·날짜·지도·시트 스타일
 *
 * 포함 스타일:
 *   - 전체 래퍼 / pull indicator / 헤더 / 뷰 토글
 *   - 날짜 네비게이션
 *   - 지도 컨테이너 / 가이드 / 바텀시트
 *   - 바텀시트 내 회사/주소/전화번호 상단 영역
 */

// ── 전체 래퍼 ─────────────────────────────────────────────────────────────────

/** 페이지 최상위 카드 래퍼 */
export const wrapStyle = {
  display: 'grid',
  gap: 12,
  border: '1px solid var(--border)',
  borderRadius: 14,
  background: 'var(--bg-card)',
  padding: 12,
  boxShadow: 'none',
};

// ── pull-to-refresh 인디케이터 ────────────────────────────────────────────────

/**
 * 당김 거리·새로고침·준비 상태에 따라 높이·색상을 동적으로 결정
 * @param {number}  distance  현재 당김 거리(px)
 * @param {boolean} refreshing 새로고침 진행 중 여부
 * @param {boolean} ready      당김 임계값 도달 여부
 */
export const pullIndicatorStyle = (distance, refreshing, ready) => ({
  height: refreshing ? 30 : Math.max(20, Math.min(34, Math.round(distance * 0.4))),
  marginBottom: 2,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 12, fontWeight: 700,
  color: refreshing ? 'var(--mm-tone-blue-text)' : (ready ? 'var(--mm-tone-amber-text)' : 'var(--text-muted)'),
  background: refreshing ? 'var(--mm-tone-blue-bg)' : 'var(--bg-subtle)',
  border: '1px solid var(--border)',
  borderRadius: 999,
  userSelect: 'none',
});

// ── 헤더 ──────────────────────────────────────────────────────────────────────

/** 상단 헤더 행: 제목(좌) + 뷰 토글(우) */
export const headerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
};

/** 헤더 제목 텍스트 */
export const titleStyle = { fontSize: 16, fontWeight: 800, color: 'var(--text-sub)' };

/** 뷰 토글(지도/목록) 컨테이너 */
export const toggleWrapStyle = {
  display: 'inline-flex', padding: 3, gap: 2, borderRadius: 10,
  background: 'var(--bg-subtle)',
};

/**
 * 뷰 토글 버튼: 활성 여부에 따라 흰 배경 카드 / 투명 배경 전환
 * @param {boolean} active 활성 탭 여부
 */
export const toggleBtnStyle = (active) => ({
  border: 'none', borderRadius: 8, padding: '6px 14px',
  fontSize: 12, fontWeight: 700, cursor: 'pointer',
  background: active ? 'var(--bg-card)' : 'transparent',
  color: active ? 'var(--text-sub)' : 'var(--text-muted)',
  boxShadow: 'none',
  transition: 'all 0.15s',
});

// ── 날짜 네비게이션 ────────────────────────────────────────────────────────────

/** 날짜 네비게이션 행: 이전 버튼 · 날짜 중앙 · 다음 버튼 */
export const dateNavRowStyle = {
  display: 'grid', gridTemplateColumns: '40px 1fr 40px', alignItems: 'center', gap: 10,
};

/** 날짜 이동 화살표 버튼 */
export const dateArrowBtnStyle = {
  border: '1px solid var(--border)', borderRadius: 999, background: 'var(--bg-card)', color: 'var(--text-sub)',
  width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: 'none', cursor: 'pointer',
};

/** 화살표 아이콘 */
export const dateArrowIconStyle = { fontSize: 20, lineHeight: 1, fontWeight: 700, marginTop: -1 };

/** 날짜 중앙 영역 (날짜 숫자 + 오늘 배지 + 요일) */
export const dateCenterWrapStyle = {
  minHeight: 42, display: 'grid', alignContent: 'center', justifyItems: 'center', gap: 2,
};

/** 날짜 숫자 (주요 레이블) — 누르면 달력이 열린다(2026-09-09) */
export const dateMainLabelStyle = {
  fontSize: 20, lineHeight: 1.1, fontWeight: 900, color: 'var(--text-sub)', letterSpacing: -0.3,
  position: 'relative', display: 'inline-block', cursor: 'pointer',
};

/** 날짜 라벨 위에 겹쳐 두는 투명 date input — 라벨을 누르면 달력이 뜬다.
 *   글자를 그대로 보여주면서 클릭 영역만 확보하려고 투명하게 덮는다. */
export const datePickerInputStyle = {
  position: 'absolute', inset: 0, width: '100%', height: '100%',
  opacity: 0, cursor: 'pointer', border: 'none', padding: 0, margin: 0,
};

/** 날짜 아래 보조 행: 오늘 배지 + 요일 */
export const dateMetaRowStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
};

/** "오늘" 텍스트 배지 (당일에만 표시) */
export const dateTodayBadgeStyle = {
  display: 'inline-block', color: 'var(--mm-tone-amber-text)', fontSize: 12, fontWeight: 800, letterSpacing: 0.2,
};

/** 요일 텍스트 */
export const dateWeekdayStyle = { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' };

// ── 지도 컨테이너 + 바텀시트 ──────────────────────────────────────────────────

/**
 * 지도 래퍼: 모바일/데스크탑 높이 차등, 그라데이션 배경
 * @param {boolean} isMobile 모바일 여부
 */
export const mapWrapStyle = (isMobile) => ({
  position: 'relative', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden',
  height: isMobile ? '64vh' : '68vh', minHeight: 420,
  background: 'var(--bg-subtle)',
});

/** 지도 하단 안내 텍스트 (반투명 배경) */
export const mapGuideStyle = {
  position: 'absolute', left: 10, right: 10, bottom: 10, zIndex: 2,
  fontSize: 12, color: 'var(--text-muted)',
  background: 'color-mix(in srgb, var(--bg-card) 92%, transparent)',
  border: '1px solid var(--border)', borderRadius: 10, padding: '7px 10px',
  backdropFilter: 'blur(6px)',
};

/**
 * 바텀시트 래퍼: 지도 하단에 절대 배치
 * @param {boolean} isMobile 모바일 여부 (모서리 둥글기 차이)
 */
export const sheetStyle = (isMobile) => ({
  position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 5,
  background: 'var(--bg-card)',
  borderTop: '1px solid var(--border)',
  borderRadius: isMobile ? '20px 20px 0 0' : '16px 16px 0 0',
  padding: '0 14px 16px',  // 상단 패딩 제거 (핸들 영역이 담당)
  boxShadow: '0 -6px 24px rgba(0,0,0,0.13)',
  display: 'grid', gap: 8,
  overscrollBehavior: 'contain', // 시트 내 스크롤이 배경으로 전파 방지
  touchAction: 'pan-y',          // 수직 스크롤은 시트 내에서만
});

/** 바텀시트 핸들 바 */
export const sheetHandleStyle = {
  width: 38, height: 4, borderRadius: 99, background: 'var(--border)', margin: '0 auto 4px',
};

/** 바텀시트 상단 행: 회사명(좌) + 닫기 버튼(우) */
export const sheetTopRowStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8,
};

/** 회사명 텍스트: 말줄임 처리 */
export const companyStyle = {
  fontSize: 16, fontWeight: 800, color: 'var(--text-sub)', overflow: 'hidden',
  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

/** 보조 텍스트 (주소·기타 소형 정보) */
export const subTextStyle = { color: 'var(--text-muted)', fontSize: 12, marginTop: 1 };

/** 상품권 뱃지 */
export const ticketBadgeStyle = {
  fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap',
  flexShrink: 0,
  background: 'var(--mm-tone-amber-bg)',
  color: 'var(--mm-tone-amber-text)',
  border: '1px solid var(--mm-tone-amber-border)',
};

/** 주소 행: 주소 텍스트(flex:1) + 복사 아이콘 버튼 */
export const addressRowStyle = {
  display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 6,
};

/** 주소 텍스트 */
export const addressTextStyle = { color: 'var(--text-muted)', fontSize: 13, wordBreak: 'break-word' };

/** 주소 복사 아이콘 버튼 */
export const copyIconBtnStyle = {
  width: 30, height: 30, border: 'none', borderRadius: 8, background: 'transparent',
  color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: 0, cursor: 'pointer',
};
